import { Alignment, Button, ButtonGroup, ButtonVariant, Divider, Icon, Intent, Popover, Position, Tag } from "@blueprintjs/core"
import { DIALOG_SIZE, WINDOW_WIDTH } from "src/libs/interfaces/layouts"
import { ClipboardCopyBtn, ConfirmButton, ScalableDialog } from "src/libs/useful"
import { __ } from "src/libs/utilities"
import useScreenSize from "src/modules/pe-basic-module/hooks/useScreenSize"
import { IHistoryStep, LANDING_EDIT_MODE } from "src/modules/pe-landing-module/data/interfaces"
import { ILandingConsoleStore, ILandingStore, useLandingConsoleStore, useLandingStore } from "src/modules/pe-landing-module/data/store"
import { allPanelData } from "../../UIX/PanelLayout"
import { IPanel, IPanelConsole } from "../../UIX/interface"
import { IPanelStore, usePanelStore } from "../../UIX/store"
import doShoWPanelData from "../../UIX/utils/doShowPanelData"
import DisplaySizeNavigator from "./DisplaySizeNavigator"
import HotkeysList from "./HotkeysList"
import UpdateSlider from "./UpdateSlider"
import redo from "./utils/redo"
import { isExists } from "./utils/saveLocal"
import undo from "./utils/undo"

import lines from "../../../assets/img/arrows-to-line.svg"
import panel_icon from "../../../assets/img/dashboard-panel.svg"
import gears_icon from "../../../assets/img/gears.svg"
import keyboard_icon from "../../../assets/img/keyboard.svg"
import page_icon from "../../../assets/img/page-settings.svg"
import sensor_icon from "../../../assets/img/sensor.svg"
import settings_icon from "../../../assets/img/settings.svg"
import tool_panel_icon from "../../../assets/img/tools_panel.svg"
import top_page_icon from "../../../assets/img/top_panel.svg"
import save from "./utils/save"

export interface IEditSwitcherProps {
	isEdit: boolean
	onSwitch: (isEdit: boolean) => void
}
const EditSwitcher = ( props: IEditSwitcherProps ) : JSX.Element => { 
	const sizes = useScreenSize()
	return sizes.width > WINDOW_WIDTH.MOBILE
		?
		<EditSwitcherScreen {...props} />
		:
		<EditSwitcherMobile {...props} />
}

const EditSwitcherMobile = ( props: IEditSwitcherProps ) : JSX.Element => { 
	return <div className="landing-edit-switcher">
		{
			props.isEdit
				?
				<>
					<Button
						intent={ Intent.DANGER }
						icon={ "eye-open" }
						variant="solid"
						onClick={ () => props.onSwitch( !props.isEdit ) } 
					> 
					</Button>
				</>
				:
				<>
					<Button
						intent={ Intent.DANGER }
						icon={ "edit" }
						variant="solid"
						onClick={ () => props.onSwitch( !props.isEdit ) } 
					> 
					</Button>
				</> 

		}
	</div>
}
const EditSwitcherScreen = ( props: IEditSwitcherProps ) : JSX.Element => { 
	const needUpdate					= useLandingStore((state: ILandingStore) => state.needUpdate ) 
	const modals: string[]				= useLandingStore((state: ILandingStore) => state.modals ) 
	const currentLanding				= useLandingStore((state: ILandingStore) => state.currentLanding ) 
	const isLocal: boolean				= useLandingStore((state: ILandingStore) => state.isLocal )  
	const history 						= useLandingStore((state: ILandingStore) => state.history ) 
	const currentHistoryStep 			= useLandingStore((state: ILandingStore) => state.currentHistoryStep ) 
	const editMode  					= useLandingConsoleStore((state:ILandingConsoleStore) => state.editMode )
	const settings  					= useLandingConsoleStore((state:ILandingConsoleStore) => state.settings )
	const consoles: IPanelConsole[] 	= usePanelStore((state: IPanelStore) => state.consoles )
	const float: IPanel[]           	= usePanelStore((state: IPanelStore) => state.float ) 
    const isHidePanels: boolean     	= usePanelStore((state: IPanelStore) => state.isHidePanels)
	
	const getCheckIcon = (key: string): JSX.Element => { 
		const isCheck = !!consoles.filter(console => !!console.panels.filter(panel => !!panel.panelDatas.filter(pd => pd.id === key).length).length ).length ||
		!!float.filter(panel => !!panel.panelDatas.filter(pd => pd.id === key).length).length
		return <Icon icon={ isCheck ? "tick" : "blank"} size={8} className="mr-2" intent={Intent.SUCCESS} /> 
	}
	const onUpdate = () => {
		useLandingStore.setState({needUpdate: !needUpdate})
	}
	const onEditLanding = () => {
		useLandingStore.setState({ isSectionEdit: true, sectionEditTab: "main", schema: "Landing", currentEditData: { ...currentLanding } })
	}
	const onEditLandingPresets = () => {
		useLandingStore.setState({ isSectionEdit: true, sectionEditTab: "presets", schema: "Landing", currentEditData: { ...currentLanding } })
	}
	const onEditLandingStyles = () => {
		useLandingStore.setState({ isSectionEdit: true, sectionEditTab: "style", schema: "Landing", currentEditData: { ...currentLanding } })
	}
	const onSettings = (setting: string, value: any) => {
		useLandingConsoleStore.setState( { settings: { ...settings, [setting]: value } } )
	} 
	const onShowPanels = ( ) => {
		usePanelStore.setState( { isHidePanels: !isHidePanels } )
	} 
	const onShowLines = () => {
		const boo = !settings.linesAndHandlers 
		useLandingConsoleStore.setState( { 
			settings: { 
				...settings, 
				linesAndHandlers: boo,
				meatContainerHandler: boo,
				meatSizes: boo,
				sectionHeight: boo,
				sectionPaddings: boo,
			} 
		} )
	}
	const onModal = (key: string) => {
		const isOpen = modals.includes(key) 
		useLandingStore.setState({
			modals: isOpen 
				? 
				modals.filter((m: string) => m !== key) : [modals, key]
		}) 
	}

	const clearHistory = () => {
		useLandingStore.setState({
			history: [],
			currentHistoryStep: 0,
			needUpdate: true,
			currentLanding: {
				...currentLanding,
				history: [],
				currentHistoryStep: 0
			}
		})
	}

	return <div className="landing-edit-switcher">
		{
			props.isEdit
				?
				<>
					<Popover
						modifiers={{ 
							arrow: { enabled: false },
							flip: { enabled: true },
							preventOverflow: { enabled: false },
							offset: { enabled: true, options: {
								offset: [ 3, 4],
							} }, 
						}}
						position={Position.BOTTOM_LEFT}
						popoverClassName="z-index-100"
						interactionKind="hover"
						content={
							<div className="small d-flex align-items-center letter-spacing-1 px-2 py-1">
								{ __( "Preview" ) }
								<Tag minimal className="ml-2 px-2 py-0"><div className="smallest title">CTRL + SHIFT + V</div></Tag>
							</div>
						}
					>
						<Button
							intent={ Intent.DANGER }
							icon={  props.isEdit ? "eye-open" : "edit" }
							variant="solid"
							onClick={ () => props.onSwitch( !props.isEdit ) } 
						/>
					</Popover> 
					<div className="position-relative">
						<Popover
							modifiers={{ 
								arrow: { enabled: false },
								flip: { enabled: true },
								preventOverflow: { enabled: false },
								offset: { enabled: true, options: {
									offset: [ 3, 4],
								} }, 
							}}
							popoverClassName="z-index-100"
							position={Position.BOTTOM_LEFT}
							interactionKind="hover"
							content={
								<div className="small d-flex align-items-center letter-spacing-1 px-2 py-1">
									{ __( "Update page manually" ) }
									<Tag minimal className="ml-2 px-2 py-0"><div className="smallest title">CTRL + S</div></Tag>
								</div>
							}
							className={`need-update-container ${ !!needUpdate ? "active" : "" }`}
						>
							{ 
									isExists()
										?
										<Button
											intent={ Intent.DANGER } 
											variant="solid"
											onClick= { 
												() => save( 
													JSON.stringify(
														{
															...currentLanding,
															history,
															currentHistoryStep, 
														}
													)
												) 
											} 
										>
											<div className="small">
												<i className="far fa-save fa-2x" />
											</div>
										</Button>
										:
										<ConfirmButton
											buttonIntent={ Intent.DANGER } 
											buttonFill 
											onConfirm={ onUpdate }  
											buttonStyle={{ width: 35 }}
											buttonLabel={
												<div className="small">
													<i className="far fa-save fa-2x" />
												</div>
											}
											isHiddenYes
											isHiddenNo
										>
											<div className="p-5">
												<ClipboardCopyBtn 
													label="Copy to clipboard" 
													data={{
														...currentLanding,
														history,
														currentHistoryStep, 
													}} 
													answer="Landing data copied to clipboard"
													type="button" 
													className="lead p-5" 
													fill 
													isNotTrim
													intent="danger"  
													variant="solid" 
													onClick={ onUpdate }
												/>
											</div>
										</ConfirmButton> 
							}
							
						</Popover>
						<UpdateSlider />
					</div>  
					
					<DisplaySizeNavigator isPosition /> 
					<Popover
						modifiers={{
							arrow: { enabled: false },
							flip: { enabled: true },
							preventOverflow: { enabled: false },
							offset: { enabled: true, options: {
								offset: [ 45, 4],
							} }, 
						}}
						position={Position.BOTTOM_RIGHT}
						popoverClassName="z-index-100"
						interactionKind="hover"
						content={
							<div>
								<div className="small letter-spacing-1 opacity_5 px-4 py-1">
									{__("Landing settings")}
								</div>
								<ButtonGroup fill vertical>
									<Button  
										alignText={ Alignment.START }
										variant={ ButtonVariant.MINIMAL }
										className="cog"
										onClick={ onEditLanding }
									>
										<div className="d-flex align-items-center">
											<Icon icon={"blank" } size={ 8 } className="mr-2" intent={ Intent.SUCCESS } />
											<img src={page_icon} alt="" height={14} className="mr-2 svg-force-contrast" />
											<div className="small w-100 d-flex justify-content-between align-items-center">
												{ __( "Page settings" ) }
												<Tag minimal className="ml-2 px-2 py-0"><div className="smallest title">ALT + A</div></Tag>
											</div>
										</div> 
									</Button>
									<Button  
										alignText={ Alignment.START }
										variant={ ButtonVariant.MINIMAL }
										className="cog"
										onClick={ onEditLandingPresets }
									>
										<div className="d-flex align-items-center">
											<Icon icon={"blank" } size={ 8 } className="mr-2" intent={ Intent.SUCCESS } />
											<img src={page_icon} alt="" height={14} className="mr-2 svg-force-contrast" />
											<div className="small w-100 d-flex justify-content-between align-items-center">
												{ __( "Page presets" ) }
												<Tag minimal className="ml-2 px-2 py-0"><div className="smallest title">ALT + P</div></Tag>
											</div>
										</div> 
									</Button>
									<Button  
										alignText={ Alignment.START }
										variant={ ButtonVariant.MINIMAL }
										className="cog"
										onClick={ onEditLandingStyles }
									>
										<div className="d-flex align-items-center">
											<Icon icon={"blank" } size={ 8 } className="mr-2" intent={ Intent.SUCCESS } />
											<img src={page_icon} alt="" height={14} className="mr-2 svg-force-contrast" />
											<div className="small w-100 d-flex justify-content-between align-items-center">
												{ __( "Page styles" ) }
												<Tag minimal className="ml-2 px-2 py-0"><div className="smallest title">ALT + S</div></Tag>
											</div>
										</div> 
									</Button> 
									<Divider />
									<Button  
										alignText={Alignment.START}
										variant={ButtonVariant.MINIMAL}
										className="cog"
										onClick={onShowPanels}
									>
										<div className="d-flex align-items-center">
											<Icon icon={ isHidePanels ? "blank" : "tick" } size={ 8 } className="mr-2" intent={ Intent.SUCCESS } />
											<img src={ sensor_icon } alt="" height={14} className="mr-2 svg-force-contrast" />
											<div className="small w-100 d-flex justify-content-between align-items-center">
												{ __( isHidePanels ? "Show panels" : "Hide panels" ) }
												<Tag minimal className="ml-2 px-2 py-0"><div className="smallest title">TAB</div></Tag>
											</div>
										</div> 
									</Button>
									{
										!isHidePanels &&
										<>
											<Button  
												alignText={Alignment.START}
												variant={ButtonVariant.MINIMAL}
												className="btn-stretch"
												onClick={ () => onSettings("panels", !settings.panels) }
											>
												<div className="d-flex align-items-center">
													<Icon icon={ settings.panels ? "tick" : "blank" } size={ 8 } className="mr-2" intent={ Intent.SUCCESS } />
													<img src={panel_icon} alt="" height={14} className="mr-2 svg-force-contrast" />
													<div className="small w-100 d-flex justify-content-between align-items-center">
														{ __( "Show panels" ) } 
														<Tag minimal className="ml-2 px-2 py-0"><div className="smallest title">ALT + P</div></Tag>
													</div>
												</div> 
											</Button>
											{
												!!settings.panels && <Popover
													modifiers={{
														// arrow: { enabled: false },
														flip: { enabled: true },
														preventOverflow: { enabled: false },
														offset: { enabled: true, options: {
															offset: [ 0, 2],
														} }, 
													}}
													position={Position.LEFT_BOTTOM}
													interactionKind="hover"
													portalClassName="z-index-100"
													content={
														<ButtonGroup fill vertical>
															{
																Object.keys( allPanelData() ).map( key => {
																	return <Button 
																		key={key}
																		alignText={Alignment.START}
																		variant={ButtonVariant.MINIMAL}
																		className="btn-stretch"
																		onClick={ () => doShoWPanelData( key ) }
																	>
																		<div className="d-flex align-items-center">
																			{ getCheckIcon(key) }
																			<img src={ allPanelData()[key]?.icon } alt="" height={14} className="mr-2 svg-force-contrast" />
																			{ __( allPanelData()[key]?.title ) }
																		</div> 
																	</Button>
																})
															}
															
														</ButtonGroup>
													}
												>
													<Button  
														alignText={Alignment.START}
														variant={ButtonVariant.MINIMAL}
														className="btn-stretch" 
													>
														<div className="d-flex align-items-center">
															<Icon icon={ "caret-left" } size={ 16 } className="mr-2" intent={ Intent.SUCCESS } />
															<Icon icon={ "modal" } size={ 14 } className="mr-2" intent={ Intent.SUCCESS } />
															<div className="small w-100 d-flex justify-content-between align-items-center">{ __( "Panels" ) }</div>
														</div> 
													</Button>
												</Popover>
											}
											<Button  
												alignText={Alignment.START}
												variant={ButtonVariant.MINIMAL}
												className="btn-stretch"
												onClick={ () => onSettings("topPanel", !settings.topPanel) }
											>
												<div className="d-flex align-items-center">
													<Icon icon={ settings.topPanel ? "tick" : "blank" } size={ 8 } className="mr-2" intent={ Intent.SUCCESS } />
													<img src={top_page_icon} alt="" height={14} className="mr-2 svg-force-contrast" />
													<div className="small w-100 d-flex justify-content-between align-items-center">
														{ __( "Show top panel" ) }												
														<Tag minimal className="ml-2 px-2 py-0"><div className="smallest title">ALT + T</div></Tag>
													</div>
												</div> 
											</Button>
											<Button  
												alignText={Alignment.START}
												variant={ButtonVariant.MINIMAL}
												className="btn-stretch"
												onClick={ () => onSettings("toolPanel", !settings.toolPanel) }
											>
												<div className="d-flex align-items-center">
													<Icon icon={ settings.toolPanel ? "tick" : "blank" } size={ 8 } className="mr-2" intent={ Intent.SUCCESS } />
													<img src={tool_panel_icon} alt="" height={14} className="mr-2 svg-force-contrast" />
													<div className="small w-100 d-flex justify-content-between align-items-center">
														{ __( "Show tools" ) }
														<Tag minimal className="ml-2 px-2 py-0"><div className="smallest title">ALT + O</div></Tag>
													</div>
												</div> 
											</Button>
										</>
									}
									<Divider />

									<Button  
										alignText={Alignment.START}
										variant={ButtonVariant.MINIMAL}
										className="btn-stretch"
										onClick={ onShowLines }
									>
										<div className="d-flex align-items-center">
											<Icon icon={ settings.linesAndHandlers ? "tick" : "blank" } size={ 8 } className="mr-2" intent={ Intent.SUCCESS } />
											<img src={lines} alt="" height={14} className="mr-2 svg-force-contrast" />
											<div className="small w-100 d-flex justify-content-between align-items-center">
												{ __( settings.linesAndHandlers ? "Hide all lines and handlers" : "Show all lines and handler" ) } 
												<Tag minimal className="ml-2 px-2 py-0"><div className="smallest title">ALT + L</div></Tag>
											</div>
										</div> 
									</Button>
									{
										!!settings.linesAndHandlers && 
										<ButtonGroup fill vertical>
											<Button  
												alignText={Alignment.START}
												variant={ButtonVariant.MINIMAL}
												className="btn-stretch" 
												onClick={ () => onSettings("meatContainerHandler", !settings.meatContainerHandler) }
											>
												<div className="d-flex align-items-center"> 
													<Icon icon={ settings.meatContainerHandler ? "tick" : "blank" } size={ 8 } className="mr-2" intent={ Intent.SUCCESS } />
													<Icon icon={ "horizontal-distribution" } size={ 14 } className="mr-2" intent={ Intent.SUCCESS } />
													<div className="small w-100 d-flex justify-content-between align-items-center">{ __( "Meat container handler" ) }</div>
												</div> 
											</Button>
											<Button  
												alignText={Alignment.START}
												variant={ButtonVariant.MINIMAL}
												className="btn-stretch" 
												onClick={ () => onSettings("meatSizes", !settings.meatSizes) }
											>
												<div className="d-flex align-items-center"> 
													<Icon icon={ settings.meatSizes ? "tick" : "blank" } size={ 8 } className="mr-2" intent={ Intent.SUCCESS } />
													<Icon icon={ "selection-box" } size={ 14 } className="mr-2" intent={ Intent.SUCCESS } />
													<div className="small w-100 d-flex justify-content-between align-items-center">{ __( "Meat's sizes lines" ) }</div>
												</div> 
											</Button>
											<Button  
												alignText={Alignment.START}
												variant={ButtonVariant.MINIMAL}
												className="btn-stretch" 
												onClick={ () => onSettings("sectionHeight", !settings.sectionHeight) }
											>
												<div className="d-flex align-items-center"> 
													<Icon icon={ settings.sectionHeight ? "tick" : "blank" } size={ 8 } className="mr-2" intent={ Intent.SUCCESS } />
													<Icon icon={ "vertical-distribution" } size={ 14 } className="mr-2" intent={ Intent.SUCCESS } />
													<div className="small w-100 d-flex justify-content-between align-items-center">{ __( "Section's height handler" ) }</div>
												</div> 
											</Button>
											<Button  
												alignText={Alignment.START}
												variant={ButtonVariant.MINIMAL}
												className="btn-stretch" 
												onClick={ () => onSettings("sectionPaddings", !settings.sectionPaddings) }
											>
												<div className="d-flex align-items-center"> 
													<Icon icon={ settings.sectionPaddings ? "tick" : "blank" } size={ 8 } className="mr-2" intent={ Intent.SUCCESS } />
													<Icon icon={ "vertical-inbetween" } size={ 14 } className="mr-2" intent={ Intent.SUCCESS } />
													<div className="small w-100 d-flex justify-content-between align-items-center">{ __( "Section's paddings handlers" ) }</div>
												</div> 
											</Button>
										</ButtonGroup> 
									}

									<Divider />
									<Button  
										alignText={Alignment.START}
										variant={ButtonVariant.MINIMAL}
										className="btn-stretch" 
										onClick={ () => onModal("hotkeys") }
									>
										<div className="d-flex align-items-center">
											<Icon icon={ "blank" } size={ 8 } className="mr-2" intent={ Intent.SUCCESS } />
											<img src={keyboard_icon} alt="" height={14} className="mr-2 svg-force-contrast" />
											<div className="small w-100 d-flex justify-content-between align-items-center">
												{ __( "Hotkeys" ) }
												<Tag minimal className="ml-2 px-2 py-0"><div className="smallest title">ALT + H</div></Tag>
											</div>
										</div> 
									</Button>
									<Button  
										alignText={Alignment.START}
										variant={ButtonVariant.MINIMAL}
										className="btn-stretch" 
										onClick={ () => onModal("editor-settings") }
									>
										<div className="d-flex align-items-center">
											<Icon icon={ "blank" } size={ 8 } className="mr-2" intent={ Intent.SUCCESS } />
											<img src={settings_icon} alt="" height={14} className="mr-2 svg-force-contrast" />
											<div className="small w-100 d-flex justify-content-between align-items-center">
												{ __( "Editor settings" ) }
												<Tag minimal className="ml-2 px-2 py-0"><div className="smallest title">ALT + S</div></Tag>
											</div>
										</div> 
									</Button>
								</ButtonGroup>
							</div>
						}
					>
						<Button
							intent={ Intent.NONE }
							variant="solid"
						>
							<img
								src={ gears_icon }
								className="svg-force-contrast"
								alt="" 
								height={ 18 }
							/> 
						</Button>
					</Popover>
					{
						editMode === true //|| LANDING_EDIT_MODE.EXTENDED
							? 
							<Popover
								modifiers={{
									// arrow: { enabled: false },
									flip: { enabled: true },
									preventOverflow: { enabled: false },
									offset: { enabled: true, options: {
										offset: [ 3, 4],
									} }, 
								}}
								position={Position.BOTTOM_RIGHT}
								interactionKind="hover"
								portalClassName="z-index-100"
								content={
									<ButtonGroup fill vertical>
										{
											Object.keys( allPanelData() ).map( key => {
												return <Button 
													key={key}
													alignText={Alignment.START}
													variant={ButtonVariant.MINIMAL}
													className="btn-stretch"
													onClick={ () => doShoWPanelData( key ) }
												>
													<div className="d-flex align-items-center">
														{ getCheckIcon(key) }
														<img src={ allPanelData()[key]?.icon } alt="" height={14} className="mr-2 svg-force-contrast" />
														{ __( allPanelData()[key]?.title ) }
													</div> 
												</Button>
											})
										}
										
									</ButtonGroup>
								}
							>
								<Button
									intent={ Intent.NONE }
									icon="modal"
									variant="solid"
								/>
							</Popover>
							:
							<></>
					}
					<Popover
						modifiers={{
							arrow: { enabled: false },
							flip: { enabled: true },
							preventOverflow: { enabled: false },
							offset: { enabled: true, options: {
								offset: [ 45, 4],
							} }, 
						}}
						position={Position.BOTTOM_LEFT}
						interactionKind="hover"
						content={
							<div className="small d-flex align-items-center letter-spacing-1 pl-4 pr-1 py-1">
								{__( "Undo" )}
								<Tag minimal className="ml-2 px-2 py-0"><div className="smallest title">CTRL + Z</div></Tag>
							</div>
						}
						popoverClassName="z-index-100"
						className="ml-1"
					>
						<Button
							intent={ Intent.NONE }
							icon="undo"
							variant="solid" 
							onClick={ undo }
							disabled={ !( history.length - currentHistoryStep ) }
						/>
					</Popover>
					<Popover
						modifiers={{
							arrow: { enabled: false },
							flip: { enabled: true },
							preventOverflow: { enabled: false },
							offset: { enabled: true, options: {
								offset: [ 0, 4],
							}}, 
						}}
						position={Position.BOTTOM_LEFT}
						interactionKind="hover"
						content={
							<div className="overflow-y-auto" style={{ maxHeight: "calc(100vh - 200px)" }}>
								<ButtonGroup fill vertical>
									<div className="small letter-spacing-1 opacity_5 px-4 py-1">
										{ __( "History of actions" ) }
									</div>
									{
										history.map((step: IHistoryStep, i: number) => {
											return <Button 
												key={ i }
												alignText={ Alignment.START }
												variant={ ButtonVariant.MINIMAL }
												className="btn-stretch"
												onClick={ () => {} }
												disabled={ i < currentHistoryStep }
											>
												<div className="small letter-spacing-1">  
													{ i + 1 }. { __( step.title || "step " + i ) }
												</div> 
											</Button>
										})
									}
									{
										!!history.length &&
										<Button
											alignText={ Alignment.START }
											variant={ ButtonVariant.MINIMAL }
											className="btn-stretch"
											onClick={ clearHistory } 
										>
											<div className="pl-3 smaller text-uppercase">  
												{ __("Clear history") }
											</div> 
										</Button>
									}
								</ButtonGroup>
							</div>
						}
						popoverClassName="z-index-100"
						className=""
					>
						<Button
							intent={ Intent.NONE }
							icon="history"
							variant="solid"  
							disabled={ !history.length }
						/>
					</Popover>
					<Popover
						modifiers={{
							arrow: { enabled: false },
							flip: { enabled: true },
							preventOverflow: { enabled: false },
							offset: { enabled: true, options: {
								offset: [ 5, 4],
							} }, 
						}}
						position={Position.BOTTOM_LEFT}
						interactionKind="hover"
						popoverClassName="z-index-100"
						content={
							<div className="small d-flex align-items-center letter-spacing-1 pl-4 pr-1 py-1">
								{__( "Redo" )}
								<Tag minimal className="ml-2 px-2 py-0"><div className="smallest title">CTRL + SHIFT + Z</div></Tag>
							</div>
						}
					>
						<Button
							intent={ Intent.NONE }
							icon="redo"
							variant="solid" 
							onClick={ redo }
							disabled={ currentHistoryStep === 0 }
						/>
					</Popover> 
					<ScalableDialog
						dialogSize={ DIALOG_SIZE.LITTLE }
						title={ __( "Hotkeys list" ) }
						isOpen={ !!modals.find( m => m === "hotkeys" ) }
						onClose={() => onModal("hotkeys")}
					>
						<div className="p-4 overflow-y-auto">
							<HotkeysList />
						</div>
					</ScalableDialog>
					<ScalableDialog
						dialogSize={ DIALOG_SIZE.LITTLE }
						title={ __( "Editor settings" ) }
						isOpen={ !!modals.find( m => m === "editor-settings" ) }
						onClose={() => onModal("editor-settings")}
					>
						<div className="p-4 overflow-y-auto">
							EDITOR SETTINGS
						</div>
					</ScalableDialog>
				</> 
				:
				<>
					<Button
						intent={ Intent.DANGER }
						icon={ "edit" }
						variant="solid"
						onClick={ () => props.onSwitch( !props.isEdit ) } 
					>
						{__("Start edit")}
					</Button>
					<DisplaySizeNavigator />
				</>
		}
	</div>
}

export default EditSwitcher